import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PageHero from '../../components/feature/PageHero';
import SecaoOfertaTimer from '../../components/feature/SecaoOfertaTimer';
import CountdownTimer from '../../components/feature/CountdownTimer';
import SEOHead from '../../components/feature/SEOHead';

export default function ProfissionalizacaoPage() {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Profissionalização na Contratação de Seguros",
    "description": "Aprenda a profissionalizar a contratação de seguros empresariais com processos estruturados e análise técnica especializada.",
    "url": "https://ibxbrasil.com.br/profissionalizacao-contratacao-seguros"
  };

  const marketRef = useScrollAnimation();
  const risksRef = useScrollAnimation();
  const methodRef = useScrollAnimation();
  const economyRef = useScrollAnimation();
  const insuranceTypesRef = useScrollAnimation();
  const proofRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/5511999999999?text=Olá! Gostaria de falar com um especialista sobre profissionalização da contratação de seguros.', '_blank', 'nofollow noreferrer');
  };

  const handleAnalysisClick = () => {
    const analysisSection = document.querySelector('#analise-gratuita');
    if (analysisSection) {
      analysisSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.REACT_APP_NAVIGATE('/contato');
    }
  };

  const risks = [
    {
      icon: 'ri-shield-cross-line',
      title: 'Cobertura errada para o risco real',
      description: 'Contratar coberturas genéricas sem análise do perfil de risco específico da sua operação.'
    },
    {
      icon: 'ri-file-warning-line',
      title: 'Cláusulas restritivas não percebidas',
      description: 'Limitações contratuais que só aparecem na hora do sinistro, comprometendo o ressarcimento.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Franquias e limites mal dimensionados',
      description: 'Valores de franquia inadequados ou limites de cobertura insuficientes para o patrimônio real.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Sobrepreço por falta de estratégia',
      description: 'Pagar mais do que o necessário por não ter poder de negociação técnica com seguradoras.'
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Descobrir falhas apenas no sinistro',
      description: 'Identificar problemas contratuais somente quando precisa acionar o seguro, gerando prejuízo.'
    }
  ];

  const methodSteps = [
    {
      number: '01',
      title: 'Diagnóstico do risco e do perfil',
      description: 'Análise completa do seu perfil de risco, operação e necessidades específicas de proteção.'
    },
    {
      number: '02',
      title: 'Análise técnica da apólice atual',
      description: 'Revisão detalhada da apólice vigente, identificando gaps, limitações e oportunidades.'
    },
    {
      number: '03',
      title: 'Estruturação das coberturas necessárias',
      description: 'Definição estratégica das coberturas adequadas ao seu perfil e exposição ao risco.'
    },
    {
      number: '04',
      title: 'Revisão de cláusulas e condições críticas',
      description: 'Análise minuciosa de cláusulas contratuais, exclusões e condições que impactam a proteção.'
    },
    {
      number: '05',
      title: 'Negociação estratégica e comparação técnica',
      description: 'Negociação especializada com seguradoras e comparação técnica de propostas.'
    },
    {
      number: '06',
      title: 'Implementação e acompanhamento',
      description: 'Gestão completa da implementação e acompanhamento contínuo da apólice contratada.'
    }
  ];

  const insuranceTypes = [
    {
      icon: 'ri-car-line',
      title: 'Seguro Automóvel',
      color: 'orange',
      pain: 'Franquias altas, coberturas limitadas e cláusulas restritivas que comprometem o ressarcimento.',
      analysis: 'Revisão de franquias, análise de cláusulas, avaliação de cobertura real e otimização de custo.',
      benefit: 'Proteção adequada com economia de até 30% e coberturas alinhadas ao uso real do veículo.'
    },
    {
      icon: 'ri-fire-line',
      title: 'Incêndio Empresarial',
      color: 'petrol',
      pain: 'Subavaliação patrimonial, coberturas insuficientes e risco de perda total sem ressarcimento adequado.',
      analysis: 'Análise patrimonial detalhada, avaliação de riscos operacionais e revisão de cobertura estrutural.',
      benefit: 'Proteção patrimonial completa com coberturas dimensionadas corretamente e negociação técnica.'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Vida Empresarial',
      color: 'green',
      pain: 'Capital segurado inadequado, falta de planejamento sucessório e proteção financeira insuficiente.',
      analysis: 'Estruturação de capital segurado, planejamento sucessório, revisão contratual e proteção financeira.',
      benefit: 'Segurança financeira empresarial com capital adequado e planejamento estratégico completo.'
    },
    {
      icon: 'ri-truck-line',
      title: 'Frota',
      color: 'orange',
      pain: 'Gestão ineficiente, sinistralidade alta e custos operacionais elevados sem controle técnico.',
      analysis: 'Análise de sinistralidade, gestão de riscos operacionais, otimização de coberturas e redução de custos.',
      benefit: 'Redução de até 40% nos custos de frota com gestão profissional e controle de sinistralidade.'
    },
    {
      icon: 'ri-building-line',
      title: 'Contratos Corporativos',
      color: 'petrol',
      pain: 'Complexidade contratual, múltiplas coberturas desalinhadas e falta de visão estratégica integrada.',
      analysis: 'Estruturação de programa de seguros, revisão de todas as coberturas e gestão integrada de riscos.',
      benefit: 'Programa de seguros corporativo completo com visão estratégica e economia significativa.'
    }
  ];

  const proofItems = [
    {
      icon: 'ri-shield-check-line',
      stat: '15+ anos',
      label: 'de experiência em gestão de seguros corporativos'
    },
    {
      icon: 'ri-team-line',
      stat: '500+',
      label: 'empresas atendidas com assessoria especializada'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      stat: 'R$ 50M+',
      label: 'em prêmios gerenciados anualmente'
    },
    {
      icon: 'ri-star-line',
      stat: '4.9/5',
      label: 'avaliação média dos clientes'
    }
  ];

  return (
    <>
      <SEOHead
        title="Profissionalização na Contratação de Seguros | IBX Brasil"
        description="Profissionalize a gestão de seguros da sua empresa: processos estruturados, análise técnica, governança e otimização de coberturas empresariais."
        canonical="https://ibxbrasil.com.br/profissionalizacao-contratacao-seguros"
        ogImage="https://readdy.ai/api/search-image?query=professional%20business%20insurance%20procurement%20process%20with%20structured%20analysis%20and%20corporate%20governance%20framework&width=1200&height=630&seq=ibx-profissionalizacao-og&orientation=landscape"
        schema={webPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <PageHero
          badge="GESTÃO DE RISCOS • CONTRATAÇÃO PROFISSIONAL"
          title="Negociar seguro sozinho hoje pode estar custando caro — em preço e em proteção"
          highlightedWord="proteção"
          highlightColor="orange"
          subtitle="A contratação de seguros mudou. Hoje envolve cláusulas técnicas, limitações e negociações complexas. A IBX Brasil profissionaliza esse processo para reduzir custos e evitar falhas contratuais."
          ctaText="Solicitar análise da minha apólice"
          ctaLink="#analise-gratuita"
          ctaSecondary={{
            text: 'Falar com especialista',
            link: 'https://wa.me/5511999999999?text=Olá! Gostaria de falar com um especialista sobre profissionalização da contratação de seguros.'
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=modern%20professional%20business%20insurance%20office%20with%20documents%20contracts%20and%20strategic%20planning%20materials%20on%20desk%20clean%20minimalist%20corporate%20environment%20with%20soft%20natural%20lighting%20and%20professional%20atmosphere%20blue%20teal%20color%20scheme&width=1920&height=700&seq=prof-hero-bg-002&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* O que mudou no mercado */}
        <section ref={marketRef} className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-800 mb-4">
                O mercado ficou mais técnico do que parece
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                A contratação de seguros evoluiu significativamente nos últimos anos, tornando-se mais complexa e técnica.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl p-8 border border-orange-200">
                <div className="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-petrol-800 mb-3">
                  Condições e cláusulas variam muito entre seguradoras
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Cada seguradora possui suas próprias condições contratuais, exclusões e limitações. O que parece igual no preço pode ser completamente diferente na proteção real.
                </p>
              </div>

              <div className="bg-gradient-to-br from-petrol-50 to-petrol-100/50 rounded-2xl p-8 border border-petrol-200">
                <div className="w-14 h-14 bg-petrol-600 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-shield-cross-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-petrol-800 mb-3">
                  Coberturas podem ter limitações decisivas
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Sublimites, franquias, exclusões e condições especiais podem comprometer completamente o ressarcimento em caso de sinistro.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl p-8 border border-green-200">
                <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-petrol-800 mb-3">
                  Preço sem leitura contratual pode gerar falsa economia
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Contratar apenas pelo menor preço sem análise técnica pode resultar em coberturas inadequadas e prejuízos significativos no futuro.
                </p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-2xl p-8 border border-red-200">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-alarm-warning-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-petrol-800 mb-3">
                  O risco aparece na hora do sinistro
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Muitas falhas contratuais só são descobertas quando é necessário acionar o seguro, gerando frustração e prejuízo financeiro.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Riscos de negociar sozinho */}
        <section ref={risksRef} className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-800 mb-4">
                5 riscos comuns de contratar sem assessoria técnica
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Entenda os principais problemas que podem comprometer sua proteção e gerar prejuízos financeiros.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {risks.map((risk, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 border-2 border-red-200 hover:border-red-400 transition-all hover:shadow-lg"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${risk.icon} text-2xl text-red-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-petrol-800 mb-2">
                    {risk.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {risk.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* O que é profissionalização */}
        <section ref={methodRef} className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-800 mb-4">
                Profissionalizar não é "comprar seguro". É estruturar proteção.
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Conheça o método IBX de profissionalização da contratação de seguros em 6 etapas estratégicas.
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-600 via-petrol-600 to-green-600 hidden lg:block"></div>

              <div className="space-y-8">
                {methodSteps.map((step, index) => (
                  <div key={index} className="relative">
                    <div className="lg:ml-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 hover:border-orange-300 transition-all hover:shadow-lg">
                      <div className="absolute -left-12 top-8 w-16 h-16 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center shadow-lg hidden lg:flex">
                        <span className="text-white font-bold text-xl">{step.number}</span>
                      </div>
                      <div className="lg:hidden w-12 h-12 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center mb-4">
                        <span className="text-white font-bold text-lg">{step.number}</span>
                      </div>
                      <h3 className="text-xl font-bold text-petrol-800 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Economia e eficiência */}
        <section ref={economyRef} className="py-20 bg-gradient-to-br from-petrol-900 via-petrol-800 to-petrol-700 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Economia real exige método
              </h2>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-white"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-lg leading-relaxed text-gray-100">
                      Em análises comparativas internas feitas pela IBX Brasil, há casos em que a diferença entre uma contratação sem assessoria e uma contratação profissionalizada é relevante — podendo chegar a <span className="font-bold text-orange-400">até 43%</span> em cenários específicos.
                    </p>
                  </div>
                </div>
                <p className="text-base text-gray-300 leading-relaxed">
                  O ganho varia conforme perfil, região, risco e coberturas. Cada caso é único e requer análise técnica individualizada para identificar oportunidades reais de economia e otimização.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Redução de Custos</h3>
                <p className="text-gray-300 text-sm">
                  Economia significativa através de negociação técnica e estruturação adequada de coberturas.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Proteção Adequada</h3>
                <p className="text-gray-300 text-sm">
                  Coberturas dimensionadas corretamente para o seu perfil de risco e necessidades reais.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="w-14 h-14 bg-petrol-400 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Segurança Contratual</h3>
                <p className="text-gray-300 text-sm">
                  Revisão completa de cláusulas e condições para evitar surpresas no sinistro.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Seção de Oferta com Timer */}
        <SecaoOfertaTimer location="secao-oferta" />

        {/* Para quais seguros vale */}
        <section ref={insuranceTypesRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-800 mb-4">
                Funciona para Auto, Empresa e Vida — e não só
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                A profissionalização da contratação se aplica a diversos tipos de seguros, cada um com suas particularidades e oportunidades.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {insuranceTypes.map((type, index) => (
                <div
                  key={index}
                  className={`bg-gradient-to-br ${
                    type.color === 'orange' ? 'from-orange-50 to-orange-100/50 border-orange-200' :
                    type.color === 'petrol' ? 'from-petrol-50 to-petrol-100/50 border-petrol-200' :
                    'from-green-50 to-green-100/50 border-green-200'
                  } rounded-2xl p-8 border-2 hover:shadow-xl transition-all`}
                >
                  <div className={`w-14 h-14 ${
                    type.color === 'orange' ? 'bg-orange-600' :
                    type.color === 'petrol' ? 'bg-petrol-600' :
                    'bg-green-600'
                  } rounded-xl flex items-center justify-center mb-6`}>
                    <i className={`${type.icon} text-2xl text-white`}></i>
                  </div>

                  <h3 className="text-2xl font-bold text-petrol-800 mb-6">
                    {type.title}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-error-warning-line text-red-600"></i>
                        <h4 className="font-bold text-petrol-800 text-sm">Dor/Risco Típico</h4>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {type.pain}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-search-line text-petrol-600"></i>
                        <h4 className="font-bold text-petrol-800 text-sm">O que a IBX Analisa</h4>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {type.analysis}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-checkbox-circle-line text-green-600"></i>
                        <h4 className="font-bold text-petrol-800 text-sm">Benefício Direto</h4>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {type.benefit}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Prova social */}
        <section ref={proofRef} className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-800 mb-4">
                O que clientes ganham com a IBX Brasil
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Números que comprovam nossa experiência e compromisso com resultados reais.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {proofItems.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 text-center border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="text-3xl font-bold text-petrol-800 mb-2">
                    {item.stat}
                  </div>
                  <p className="text-gray-600 text-sm">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-double-quotes-l text-2xl text-green-600"></i>
                </div>
                <div>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    "A IBX Brasil transformou completamente nossa gestão de seguros. Identificaram falhas contratuais que poderiam ter gerado prejuízos significativos e conseguiram reduzir nossos custos em 35% mantendo coberturas mais adequadas."
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-petrol-600 to-petrol-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">RC</span>
                    </div>
                    <div>
                      <div className="font-bold text-petrol-800">Roberto Costa</div>
                      <div className="text-sm text-gray-600">Diretor Financeiro, Empresa de Logística</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section ref={ctaRef} className="py-20 bg-gradient-to-br from-orange-600 via-orange-700 to-orange-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Pronto para profissionalizar sua contratação de seguros?
            </h2>
            <p className="text-xl text-orange-100 mb-10 max-w-2xl mx-auto">
              Solicite uma análise gratuita da sua apólice atual e descubra oportunidades de economia e proteção adequada.
            </p>
            
            {/* Countdown Timer */}
            <div className="mb-8">
              <CountdownTimer location="profissionalizacao" />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Solicitar Análise Gratuita
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <button
                onClick={handleWhatsAppClick}
                className="w-full sm:w-auto bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all hover:scale-105 whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Falar no WhatsApp
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-petrol-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-600 to-orange-700 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">IBX</span>
                  </div>
                  <span className="font-bold text-xl">IBX Brasil</span>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Especialistas em gestão profissional de seguros corporativos.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-4">Serviços</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li><a href="/servicos/automovel" className="hover:text-orange-400 transition-colors">Seguro Automóvel</a></li>
                  <li><a href="/servicos/vida-empresarial" className="hover:text-orange-400 transition-colors">Vida Empresarial</a></li>
                  <li><a href="/servicos/consultoria-tecnica" className="hover:text-orange-400 transition-colors">Consultoria Técnica</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-4">Empresa</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li><a href="/quem-somos" className="hover:text-orange-400 transition-colors">Quem Somos</a></li>
                  <li><a href="/blog" className="hover:text-orange-400 transition-colors">Blog</a></li>
                  <li><a href="/contato" className="hover:text-orange-400 transition-colors">Contato</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-4">Contato</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    <span>(11) 99999-9999</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-mail-line"></i>
                    <span>contato@ibxbrasil.com</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-8 text-center">
              <p className="text-sm text-gray-300">
                © 2025 IBX Brasil. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </footer>

        {/* WhatsApp Fixo */}
        <a
          href="https://wa.me/5511999999999?text=Olá! Gostaria de saber mais sobre profissionalização da contratação de seguros."
          target="_blank"
          rel="nofollow noreferrer"
          className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-all hover:scale-110 z-40"
          aria-label="Falar no WhatsApp"
        >
          <i className="ri-whatsapp-line text-2xl text-white"></i>
        </a>
      </div>
    </>
  );
}